"use client";

import React from "react";
import { ShieldCheck, Plane, Receipt as ReceiptCheck, ArrowRight, Clock, Sparkles, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const ratePillars = [{
  icon: ShieldCheck,
  tag: "Absolute Certainty",
  title: "Zero Surge Pricing",
  description: "Your quote is locked the moment you confirm. Peak rush hours, sudden rainstorms, or highway backups never inflate your fare.",
  metric: "0% Surge",
  subtext: "Guaranteed locked quotes"
}, {
  icon: Plane,
  tag: "Airport Logistics",
  title: "Flight-Synchronized Transfers",
  description: "Chauffeurs continuously monitor live flight telemetry at O'Hare, Midway, and regional FBOs, adjusting curbside arrival in real time.",
  metric: "60 Min",
  subtext: "Complimentary flight buffer"
}, {
  icon: ReceiptCheck,
  tag: "All-Inclusive",
  title: "Transparent Tolls & Fees",
  description: "Every expressway toll, state transit fee, and airport access charge is itemized upfront before booking. No undisclosed add-ons.",
  metric: "100%",
  subtext: "Transparent upfront invoice"
}];
const standardIncludes = ["Flat city-to-airport transfers", "Luggage assistance & door opening", "Complimentary chilled artisan water", "Direct chauffeur text & vehicle tracking"];
export default function FlatRate() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        duration: shouldReduceMotion ? 0 : 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0 : 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="flat-rate-promise" data-nav="dark" className="dark bg-background text-foreground relative overflow-hidden py-24 md:py-32">
      {/* Background imagery with atmospheric dark scrim */}
      <div className="absolute inset-0 -z-20">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/dusk_airport_highway_skyline.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/dusk_airport_highway_skyline.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/dusk_airport_highway_skyline.png-wvc-sizes" alt="Atmospheric dusk highway corridor approaching an executive airport terminal" className="h-full w-full object-cover object-center opacity-20" loading="lazy" decoding="async" />
      </div>

      {/* Surface illumination overlays */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-background via-background/90 to-background" />
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_600px_at_50%_35%,hsla(42,65%,54%,0.08),transparent_75%)]" />

      <div className="mx-auto max-w-6xl px-6 md:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="space-y-12">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto">
            <motion.div variants={itemVariants} className="inline-flex items-center justify-center">
              <span className="inline-flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-semibold tracking-wider text-primary uppercase">
                <Sparkles className="size-3.5" />
                Transparent Fare Guarantee
              </span>
            </motion.div>

            <motion.h2 variants={itemVariants} className="mt-6 text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
              Guaranteed flat rates on{" "}
              <span className="italic text-primary">every</span> journey.
            </motion.h2>

            <motion.p variants={itemVariants} className="mt-4 text-base text-muted-foreground md:text-lg leading-relaxed">
              Experience serene executive ground travel across Chicago, Illinois, and neighboring
              corridors with permanent flat-rate confidence. No fluctuating meters, no congestion
              spikes, and zero hidden line items.
            </motion.p>
          </div>

          {/* Core Feature Cards Matrix */}
          <motion.div variants={itemVariants} className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {ratePillars.map((pillar, index) => {
            const Icon = pillar.icon;
            return <div key={pillar.title} data-index={index} className="group relative flex flex-col justify-between rounded-xl border border-border bg-card/90 p-7 md:p-8 shadow-xl backdrop-blur-sm transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-2xl">
                  {/* Hairline highlight along top edge */}
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent group-hover:via-primary/60 transition-opacity" />

                  <div>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex size-11 items-center justify-center rounded-lg border border-border bg-secondary text-primary transition-colors group-hover:border-primary/40">
                        <Icon className="size-5" />
                      </div>
                      <span className="rounded-lg border border-border/80 bg-secondary/50 px-2.5 py-1 text-xs font-medium text-muted-foreground">
                        {pillar.tag}
                      </span>
                    </div>

                    <h3 className="mt-6 text-xl font-semibold text-foreground">
                      {pillar.title}
                    </h3>

                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      {pillar.description}
                    </p>
                  </div>

                  {/* Quantitative Metric Callout */}
                  <div className="mt-8 border-t border-border/80 pt-5 flex items-baseline justify-between">
                    <div>
                      <div className="text-2xl font-bold font-mono tracking-tight text-primary">
                        {pillar.metric}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {pillar.subtext}
                      </div>
                    </div>
                    <Clock className="size-4 text-muted-foreground/60" />
                  </div>
                </div>;
          })}
          </motion.div>

          {/* Included Standards Strip & Booking Action */}
          <motion.div variants={itemVariants} className="relative rounded-xl border border-border/80 bg-card/60 p-6 md:p-8 backdrop-blur-sm">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              {/* Inclusion checklist */}
              <div className="w-full lg:w-auto">
                <p className="text-xs font-semibold tracking-wider text-primary uppercase mb-3">
                  Included In Every Scheduled Chauffeur Ride
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-foreground">
                  {standardIncludes.map((item, index) => <div key={item} data-index={index} className="flex items-center gap-2.5">
                      <CheckCircle2 className="size-4 text-primary shrink-0" />
                      <span>{item}</span>
                    </div>)}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="w-full lg:w-auto shrink-0 flex flex-col sm:flex-row items-center gap-3">
                <Button asChild size="lg" className="group w-full sm:w-auto h-12 bg-primary px-8 text-sm font-semibold text-primary-foreground shadow-lg transition-[background-color,box-shadow] duration-200 hover:bg-primary/90 hover:shadow-primary/20">
                  <Link to="/OrderPage">
                    <span>Reserve With Flat Rate</span>
                    <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="w-full sm:w-auto h-12 border-border bg-card/60 px-6 text-sm font-semibold text-foreground hover:bg-secondary hover:text-foreground">
                  <Link to="/AboutUsPage">Our Service Standards</Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}